import { PageContainer } from '@/components/PageContainer';
import { MarkdownMessage } from '@astro/shared-ui';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Heading } from '@televet/kibble-ui/Heading';
import { Text } from '@televet/kibble-ui/Text';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Box, VStack, HStack, Code } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
export const Route = createFileRoute('/_authenticated/chats/$chatId')({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Chat Details',
    };
  },
});

function RouteComponent() {
  const { chatId } = Route.useParams();
  const navigate = useNavigate();

  const {
    data: result,
    isLoading,
    isError,
  } = useQuery(
    trpc.agentConversation.findOne.queryOptions({
      id: chatId,
    })
  );

  if (isLoading) {
    return (
      <PageContainer>
        <Text>Loading chat...</Text>
      </PageContainer>
    );
  }

  if (isError || !result?.conversation) {
    return (
      <PageContainer>
        <VStack gap={4}>
          <Text variant="danger">Failed to load chat</Text>
          <Button onClick={() => navigate({ to: '/chats' })}>
            Back to Chats
          </Button>
        </VStack>
      </PageContainer>
    );
  }

  const { conversation } = result;

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getRoleTagVariant = (role: string) => {
    switch (role.toLowerCase()) {
      case 'user':
        return 'info';
      case 'assistant':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={6}>
        {/* Header with back button */}
        <Flex justify="space-between" align="center">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate({ to: '/chats' })}
          >
            ← Back
          </Button>
          <Heading size="lg">Chat Details</Heading>
          <Box w="80px" />
        </Flex>

        {/* Conversation Info Card */}
        <Card borderColor="border.default" rounded="lg">
          <Box px={6} pt={6} pb={4}>
            <Flex direction="column" gap={4}>
              <Flex gap={4} align="center">
                <Flex direction="column" gap={2}>
                  <Text fontSize="sm" variant="subtle">
                    Agent
                  </Text>
                  <Tag label={conversation.currentAgent.name} variant="info" />
                </Flex>
                <Flex direction="column" gap={2}>
                  <Text fontSize="sm" variant="subtle">
                    Type
                  </Text>
                  <Tag label={conversation.currentAgent.type} variant="default" />
                </Flex>
              </Flex>

              <Flex direction="column" gap={2}>
                <Text fontSize="sm" variant="subtle">
                  AI Summary
                </Text>
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {conversation.aiSummary || 'No summary available'}
                </Text>
              </Flex>

              <Flex gap={4} fontSize="xs" color="text.subtle" wrap="wrap">
                <Flex direction="column">
                  <Text>Created</Text>
                  <Text fontWeight="medium">
                    {formatDate(conversation.createdAt)}
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text>Updated</Text>
                  <Text fontWeight="medium">
                    {formatDate(conversation.updatedAt)}
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text>Total Tokens</Text>
                  <Text fontWeight="medium">
                    {conversation.totalTokens > 0
                      ? conversation.totalTokens.toLocaleString()
                      : '-'}
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text>Input / Output</Text>
                  <Text fontWeight="medium">
                    {conversation.totalInputTokens > 0 ||
                    conversation.totalOutputTokens > 0
                      ? `${conversation.totalInputTokens.toLocaleString()} / ${conversation.totalOutputTokens.toLocaleString()}`
                      : '-'}
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text>Estimated Cost</Text>
                  <Text fontWeight="medium">
                    {conversation.totalCost > 0
                      ? `$${conversation.totalCost.toFixed(2)}`
                      : '-'}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Card>

        {/* Messages Section */}
        <Flex direction="column" gap={4}>
          <Heading size="md">Message History</Heading>
          <VStack gap={3} align="stretch">
            {conversation.messages && conversation.messages.length > 0 ? (
              conversation.messages.map((message) => (
                <Card
                  key={message.id}
                  borderColor="border.default"
                  rounded="lg"
                  bg={message.role === 'user' ? 'bg.subtle' : 'bg.muted'}
                >
                  <Box px={6} pt={6} pb={2}>
                    <HStack gap={4} justify="space-between">
                      <Flex align="center" gap={3}>
                        <Tag
                          label={message.role.toUpperCase()}
                          variant={getRoleTagVariant(message.role)}
                        />
                        {message.user && (
                          <Text fontSize="xs" variant="subtle">
                            {message.user.firstName} {message.user.lastName}
                          </Text>
                        )}
                        {message.agent && (
                          <Text fontSize="xs" variant="subtle">
                            Agent: {message.agent.name}
                          </Text>
                        )}
                      </Flex>
                      <Text fontSize="xs" variant="subtle">
                        {formatDate(message.createdAt)}
                      </Text>
                    </HStack>
                  </Box>
                  <Box px={6} pb={6}>
                    <MarkdownMessage content={message.message} />

                    {/* Tool Calls Section */}
                    {Array.isArray(message.toolCalls) &&
                      message.toolCalls.length > 0 && (
                        <Box mt={4}>
                          <Text fontSize="xs" fontWeight="bold" mb={2}>
                            Tool Calls:
                          </Text>
                          <VStack gap={2} align="stretch">
                            {message.toolCalls.map((toolCall: any) => (
                              <Box
                                key={toolCall.id}
                                p={2}
                                bg="bg"
                                rounded="sm"
                                borderLeft="2px solid"
                                borderLeftColor="border.emphasized"
                              >
                                <HStack justify="space-between">
                                  <Text fontSize="xs" fontWeight="medium">
                                    {toolCall.tool?.name || 'Tool Call'}
                                  </Text>
                                  <Text fontSize="xs" variant="subtle">
                                    {toolCall.timeSpentInMilliseconds}ms
                                  </Text>
                                </HStack>
                                {toolCall.content && (
                                  <Box
                                    mt={1}
                                    maxH="300px"
                                    overflowY="auto"
                                    fontSize="xs"
                                  >
                                    <Code
                                      display="block"
                                      whiteSpace="pre"
                                      p={2}
                                      borderRadius="md"
                                      bg="bg.muted"
                                      color="text"
                                      overflowX="auto"
                                    >
                                      {typeof toolCall.content === 'string'
                                        ? toolCall.content
                                        : JSON.stringify(
                                            toolCall.content,
                                            null,
                                            2
                                          )}
                                    </Code>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      )}
                  </Box>
                </Card>
              ))
            ) : (
              <Text variant="subtle">No messages in this chat</Text>
            )}
          </VStack>
        </Flex>
      </Flex>
    </PageContainer>
  );
}
